import React, { memo } from 'react';
import { Checkbox, CheckboxGroup } from '@nextui-org/react';
import { QuizProps } from '../QuizModal';
import QuizControls from '../QuizControls';
import { useTranslation } from "react-i18next";


export interface McqQuiz {
    type: 'multiple_choice';
    question: string;
    answers: {
        id: string;
        text: string;
        correct: boolean;
    }[];
    times_in_video: number[];
    maxTries: number;
}

interface MultipleChoiceQuestionQuizProps extends QuizProps {
    quiz: McqQuiz;
    submission?: string[];
    setSubmission?: (submission: string[]) => void;
}

const MultipleChoiceQuestionQuiz = memo((props: MultipleChoiceQuestionQuizProps) => {
    const [selected, setSelected] = React.useState<string[]>(props.submission || []);
    const [message, setMessage] = React.useState('');
    const { t } = useTranslation();


    React.useEffect(() => {
        // Reset the selection when the quiz is initialized
        setSelected(props.submission || []);
    }, [props.submission]);

    const handleReset = () => {
        setSelected([]);
        props.setSubmitted(false);
        props.setCorrect(false);
        setMessage('');
    };

    const handleSubmission = () => {
        setMessage('');
        if (selected.length === 0) {
            setMessage(t("mcq.select_answer"));
            return;
        }
        props.setSubmitted(true);
        props.setTriesCounter(props.triesCounter + 1);
        props.setSubmission && props.setSubmission(selected);
        const correctAnswers = props.quiz.answers.filter((answer) => answer.correct).map((answer) => answer.id);
        if (selected.every((answer) => correctAnswers.includes(answer))) {
            props.setCorrect(true);
        } else {
            props.setCorrect(false);
        }
    };

    const showCorrect = () => {
        setMessage('');
        const correctAnswers = props.quiz.answers.filter((answer) => answer.correct).map((answer) => answer.id);
        setSelected(correctAnswers);
    };

    return (
        <div className='flex flex-col justify-between'>
            <div>
                <CheckboxGroup
                    label={props.quiz.question}
                    value={selected}
                    onValueChange={(value) => {
                        setSelected(value);
                        setMessage('');
                    }}
                    errorMessage={message}
                    classNames={{
                        label: 'text-foreground',
                    }}
                >
                    {props.quiz.answers.map((answer) => {
                        return (
                            <Checkbox
                                key={answer.id}
                                value={answer.id}
                                disabled={props.submitted}
                            >
                                {answer.text}
                            </Checkbox>
                        );
                    })}
                </CheckboxGroup>
            </div>
            <QuizControls selectedQuiz={props.selectedQuiz} numberOfQuiz={props.numberOfQuiz} onClose={props.onClose}
                          handleNextQuiz={props.handleNextQuiz} handlePreviousQuiz={props.handlePreviousQuiz}
                          handleSubmission={handleSubmission} handleReset={handleReset} showCorrect={showCorrect}
                          maxTries={props.quiz.maxTries} submitCount={props.triesCounter} correct={props.correct} />
        </div>
    );
});

export default MultipleChoiceQuestionQuiz;
